import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import useSWR, { mutate } from "swr";
import { api, WEEKDAYS_SK_SHORT } from "@/lib/app";
import { Plus, Storefront, Trash, ArrowRight, Sparkle, PencilSimple, Eraser, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";
import StoreFormDialog from "@/components/StoreFormDialog";
import SmartImportDialog from "@/components/SmartImportDialog";
import { useAuth } from "@/lib/auth";

const fetcher = (url) => api.get(url).then((r) => r.data);

const MONTH_NAMES = ["", "Január", "Február", "Marec", "Apríl", "Máj", "Jún", "Júl", "August", "September", "Október", "November", "December"];

function TidoArrivalCard({ stores }) {
  const { tidoContext } = useAuth();
  const navigate = useNavigate();
  const [creds, setCreds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/tido/credentials").then((r) => setCreds(r.data)).catch(() => setCreds([]));
  }, []);

  if (!tidoContext || (!tidoContext.idp && !tidoContext.rok && !tidoContext.mesiac)) return null;

  const idp = tidoContext.idp;
  const rok = tidoContext.rok;
  const mesiac = tidoContext.mesiac;
  const mesiacName = mesiac ? MONTH_NAMES[parseInt(mesiac)] : null;

  const matchedStore = idp ? stores.find((s) => String(s.tido_center_id) === String(idp)) : null;

  const hostname = window.location.hostname;
  let matchedCred = creds.find((c) => c.domain === hostname);
  if (!matchedCred && creds.length === 1) matchedCred = creds[0];
  if (!matchedCred && creds.length > 0) {
    matchedCred = creds.find((c) => c.domain.includes("otido")) || creds[0];
  }

  const loadData = async () => {
    if (!matchedCred || !idp || !rok || !mesiac) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const { data } = await api.post(
        `/tido/credentials/${encodeURIComponent(matchedCred.domain)}/fetch`,
        null,
        { params: { center_id: parseInt(idp), rok: parseInt(rok), mesiac: parseInt(mesiac) } },
      );
      setResult(data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Chyba pri načítaní");
    } finally {
      setLoading(false);
    }
  };

  const importAndOpen = async () => {
    if (!matchedCred || !idp || !rok || !mesiac) return;
    setImporting(true);
    setError(null);
    try {
      const { data } = await api.post(
        `/tido/credentials/${encodeURIComponent(matchedCred.domain)}/import`,
        null,
        { params: { center_id: parseInt(idp), rok: parseInt(rok), mesiac: parseInt(mesiac) } },
      );
      toast.success(
        `${data.created_store ? "Vytvorené stredisko" : "Aktualizované stredisko"} ${data.store_code} — ${data.store_name}. Realita: ${data.realita?.imported ?? 0}, Plán: ${data.plan?.imported ?? 0}.`,
      );
      // Refresh SWR cache of stores
      mutate("/stores");
      // Navigate to store detail with preset period
      navigate(`/strediska/${data.store_id}?period_from=${data.date_from}&period_to=${data.date_to}`);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Chyba pri importe");
      setImporting(false);
    }
  };

  return (
    <div className="mb-8 card-flat p-5 bg-[var(--brand)]/5 border-[var(--brand)]/30" data-testid="tido-arrival-card">
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-full bg-[var(--brand)]/10 text-[var(--brand)] flex items-center justify-center shrink-0">
          <ArrowSquareOut size={22} weight="fill" />
        </div>
        <div className="flex-1">
          <p className="label-eyebrow mb-1 text-[var(--brand)]">Prišiel si z TiDo stránky</p>
          <h2 className="font-display text-xl font-semibold mb-3">Parametre z odkazu:</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-0.5">IDP (center_id)</div>
              <div className="font-display text-2xl font-bold tabular text-[var(--text-primary)]" data-testid="arrival-idp">{idp || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-0.5">ROK</div>
              <div className="font-display text-2xl font-bold tabular text-[var(--text-primary)]" data-testid="arrival-rok">{rok || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-0.5">MESIAC</div>
              <div className="font-display text-2xl font-bold tabular text-[var(--text-primary)]" data-testid="arrival-mesiac">
                {mesiac || "—"}{mesiacName ? <span className="text-sm font-normal text-[var(--text-secondary)] ml-1">({mesiacName})</span> : null}
              </div>
            </div>
            {tidoContext.type && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-0.5">TYPE</div>
                <div className="font-display text-2xl font-bold tabular text-[var(--text-primary)]" data-testid="arrival-type">{tidoContext.type}</div>
              </div>
            )}
          </div>
          {matchedStore && (
            <div className="flex items-center gap-2 text-xs mb-3">
              <span className="text-[var(--success)]">✓ Prepojené so strediskom <strong>{matchedStore.code} — {matchedStore.name}</strong></span>
              <Link
                to={`/strediska/${matchedStore.id}`}
                className="btn-ghost text-xs px-3 py-1.5 inline-flex items-center gap-1"
                data-testid="arrival-open-store"
              >
                Otvoriť <ArrowRight size={12} />
              </Link>
            </div>
          )}

          <div className="pt-3 border-t border-[var(--brand)]/20">
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={importAndOpen}
                disabled={!matchedCred || !idp || !rok || !mesiac || importing || loading}
                className="btn-primary text-sm inline-flex items-center gap-1.5"
                data-testid="arrival-import-btn"
              >
                {importing ? "Importujem…" : matchedStore ? "Aktualizovať dáta a otvoriť analytiku" : "Vytvoriť stredisko + import + otvoriť analytiku"}
              </button>
              <button
                onClick={loadData}
                disabled={!matchedCred || !idp || !rok || !mesiac || loading || importing}
                className="btn-ghost text-xs"
                data-testid="arrival-load-hash"
                title="Len načítať dáta bez importu (preview)"
              >
                {loading ? "Načítavam…" : "Len preview"}
              </button>
              {matchedCred && (
                <div className="text-xs text-[var(--text-secondary)] ml-1">
                  Cez <strong>{matchedCred.domain}</strong>
                </div>
              )}
            </div>
            {error && (
              <div className="mt-3 text-xs bg-[var(--critical-bg)] text-[var(--critical)] p-3 rounded border border-[var(--critical)]/20">
                <strong>Chyba:</strong> {error}
              </div>
            )}
            {result && (
              <div className="mt-3 space-y-2" data-testid="arrival-fetch-result">
                <div className="text-[10px] uppercase tracking-wider text-[var(--success)] font-semibold">
                  ✓ Preview za obdobie {result.date_from} → {result.date_to}
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="bg-white p-2 rounded border border-[var(--border-default)]">
                    <div className="label-eyebrow">Realita</div>
                    <div className="font-display font-bold tabular text-lg" data-testid="fetch-realita-count">
                      {result.realita ? result.realita.rows?.length ?? 0 : (result.errors?.realita ? "chyba" : "—")}
                    </div>
                    <div className="text-[10px] text-[var(--text-muted)]">hodinových záznamov</div>
                  </div>
                  <div className="bg-white p-2 rounded border border-[var(--border-default)]">
                    <div className="label-eyebrow">Plán</div>
                    <div className="font-display font-bold tabular text-lg" data-testid="fetch-plan-count">
                      {result.plan ? result.plan.rows?.length ?? 0 : (result.errors?.plan ? "chyba" : "—")}
                    </div>
                    <div className="text-[10px] text-[var(--text-muted)]">hodinových záznamov</div>
                  </div>
                  <div className="bg-white p-2 rounded border border-[var(--border-default)]">
                    <div className="label-eyebrow">Shift-times</div>
                    <div className="font-display font-bold tabular text-lg" data-testid="fetch-shift-status">
                      {result.shift_times ? "✓ OK" : "—"}
                    </div>
                    <div className="text-[10px] text-[var(--text-muted)]">{result.shift_times?.center_name || ""}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function StoreList() {
  const { data: stores = [], isLoading } = useSWR("/stores", fetcher);
  const { data: companies = [] } = useSWR("/companies", fetcher);
  const [showForm, setShowForm] = useState(false);
  const [showSmart, setShowSmart] = useState(false);
  const { user } = useAuth();
  const isDeveloper = user?.role === "developer";

  const handleDelete = async (id, name) => {
    if (!confirm(`Naozaj zmazať stredisko "${name}" so všetkými dátami?`)) return;
    try {
      await api.delete(`/stores/${id}`);
      toast.success("Stredisko zmazané");
      mutate("/stores");
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Neznáma chyba";
      toast.error(`Mazanie zlyhalo: ${detail}`);
      console.error("Delete store failed:", e);
    }
  };

  const handleClearData = async (id, name) => {
    if (!confirm(`Vymazať VŠETKY hodinové dáta strediska "${name}"?\n\nKonfigurácia, poznámky a označené body ostanú zachované. Toto je užitočné pred čistým reimportom.`)) return;
    try {
      const res = await api.delete(`/stores/${id}/data`);
      toast.success(`Vymazaných ${res.data?.deleted ?? 0} záznamov`);
      mutate("/stores");
    } catch (e) {
      toast.error("Nepodarilo sa vymazať dáta");
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-8">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="label-eyebrow mb-2">Prehľad</p>
          <h1 className="font-display text-4xl font-bold tracking-tight">Strediská</h1>
          <p className="text-[var(--text-secondary)] mt-2 max-w-xl">
            Spravuj prevádzky, nahrávaj hodinové dáta o tržbách a zamestnancoch a analyzuj efektivitu.
          </p>
        </div>
        {isDeveloper && (
          <div className="flex gap-2">
            <button
              data-testid="smart-import-button"
              onClick={() => setShowSmart(true)}
              className="btn-primary inline-flex items-center gap-2"
            >
              <Sparkle size={16} weight="fill" /> Smart Import
            </button>
            <button
              data-testid="new-store-button"
              onClick={() => setShowForm(true)}
              className="btn-ghost inline-flex items-center gap-2"
            >
              <PencilSimple size={14} /> Ručne
            </button>
          </div>
        )}
      </div>

      {isLoading && <div className="text-[var(--text-secondary)]">Načítavam...</div>}

      <TidoArrivalCard stores={stores} />

      {!isLoading && stores.length === 0 && (
        <div className="card-flat p-12 text-center">
          <Storefront size={40} className="mx-auto mb-3 text-[var(--text-muted)]" />
          <h3 className="font-display text-xl font-semibold">Ešte žiadne strediská</h3>
          <p className="text-[var(--text-secondary)] mt-2">Začni vytvorením prvej prevádzky.</p>
          <button
            data-testid="empty-create-button"
            onClick={() => setShowSmart(true)}
            className="btn-primary mt-5 inline-flex items-center gap-2"
          >
            <Sparkle size={16} weight="fill" /> Smart Import (drag-and-drop súborov)
          </button>
        </div>
      )}

      <div className="space-y-8">
        {(() => {
          // Build alphabetized groups: companies A→Z, then "Nezaradené" bucket.
          const byId = new Map(companies.map((c) => [c.id, c]));
          const groups = new Map();
          const unassigned = [];
          stores.forEach((s) => {
            const c = s.company_id ? byId.get(s.company_id) : null;
            if (!c) { unassigned.push(s); return; }
            if (!groups.has(c.id)) groups.set(c.id, { company: c, stores: [] });
            groups.get(c.id).stores.push(s);
          });
          const sortedGroups = [...groups.values()]
            .sort((a, b) => (a.company.name || "").localeCompare(b.company.name || "", "sk"));
          sortedGroups.forEach((g) => g.stores.sort((a, b) => (a.name || "").localeCompare(b.name || "", "sk")));
          unassigned.sort((a, b) => (a.name || "").localeCompare(b.name || "", "sk"));

          const renderCard = (s) => {
            const cleanName = (s.name || "").replace(/\s*-\s*\d{1,2}\.\d{1,2}\.\d{4}.*$/, "").trim() || s.name;
            return (
              <div key={s.id} data-testid={`store-card-${s.code}`} className="card-flat p-5 flex flex-col">
                <div className="flex items-start justify-between mb-3">
                  <div className="min-w-0">
                    <p className="label-eyebrow mb-1">Stredisko</p>
                    <h3 className="font-display text-2xl font-bold tabular leading-tight">{s.code}</h3>
                    <p className="text-sm text-[var(--text-secondary)] mt-1 truncate" title={cleanName}>{cleanName}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      data-testid={`clear-data-${s.code}`}
                      onClick={() => handleClearData(s.id, s.name)}
                      className="text-[var(--text-muted)] hover:text-[var(--brand)] transition-colors p-1"
                      aria-label="Vymazať dáta"
                      title="Vymazať všetky hodinové dáta (config ostáva)"
                    >
                      <Eraser size={16} />
                    </button>
                    <button
                      data-testid={`delete-store-${s.code}`}
                      onClick={() => handleDelete(s.id, s.name)}
                      className="text-[var(--text-muted)] hover:text-[var(--critical)] transition-colors p-1"
                      aria-label="Zmazať stredisko"
                      title="Zmazať celé stredisko"
                    >
                      <Trash size={16} />
                    </button>
                  </div>
                </div>
                <div className="text-sm text-[var(--text-secondary)] mb-4">
                  <div>Požadovaný výkon: <span className="tabular text-[var(--text-primary)]">{s.required_hourly_performance} €/os.hod</span></div>
                  <div className="mt-1 flex gap-1 flex-wrap">
                    {WEEKDAYS_SK_SHORT.map((w, i) => {
                      const cfg = s.day_configs?.[String(i)] || {};
                      const closed = cfg.closed;
                      return (
                        <span
                          key={i}
                          className={`text-[10px] tabular px-1.5 py-0.5 rounded ${
                            closed ? "bg-[var(--bg-page)] text-[var(--text-muted)]" : "bg-[var(--success-bg)] text-[var(--success)]"
                          }`}
                          title={cfg.open_from ? `${cfg.open_from}-${cfg.open_to}` : "zatvorené"}
                        >
                          {w}
                        </span>
                      );
                    })}
                  </div>
                </div>
                <Link
                  to={`/strediska/${s.id}`}
                  data-testid={`open-store-${s.code}`}
                  className="mt-auto inline-flex items-center justify-between text-sm font-medium text-[var(--brand)] hover:text-[var(--brand-hover)] group"
                >
                  Otvoriť analytiku
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            );
          };

          return (
            <>
              {sortedGroups.map(({ company, stores: gs }) => (
                <section key={company.id} data-testid={`stores-group-${company.subdomain}`}>
                  <div className="flex items-baseline gap-3 mb-3">
                    <h2 className="font-display text-xl font-semibold">{company.name}</h2>
                    <span className="text-xs text-[var(--text-muted)] tabular">
                      {company.subdomain}.tido.sk · {gs.length} stredísk
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {gs.map(renderCard)}
                  </div>
                </section>
              ))}
              {unassigned.length > 0 && (
                <section data-testid="stores-group-unassigned">
                  <div className="flex items-baseline gap-3 mb-3">
                    <h2 className="font-display text-xl font-semibold text-[var(--text-muted)]">Nezaradené</h2>
                    <span className="text-xs text-[var(--text-muted)] tabular">
                      bez klienta · {unassigned.length} stredísk
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {unassigned.map(renderCard)}
                  </div>
                </section>
              )}
            </>
          );
        })()}
      </div>

      {showForm && <StoreFormDialog onClose={() => setShowForm(false)} />}
      {showSmart && <SmartImportDialog onClose={() => setShowSmart(false)} />}
    </div>
  );
}
